import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { environment } from '../../environments';
import { Observable, throwError as _throw } from 'rxjs';
import { map, catchError, debounceTime } from 'rxjs/operators';
import { TracingPlanItemFilter } from '../models/tracing-plan-item-filter';
import { TracingPlanStatusCount } from '../models/tracing-plan-status.count';
import { TracingPlanDetailItem } from '../models/tracing-plan-detail-item';
import { TracingPlanTaskFilter } from '../models/tracing-plan-task-filter';
import { TracingPlanTaskDetail } from '../models/tracing-plan-task-detail';
import { TracingPlanTaskItem } from '../models/tracing-plan-task-item';
import { TracingPlanStatus } from '../models/tracing-plan-status';
import { ThirdPartyFollowUpTaskExternalDto } from '../models/thirdparty-tracing-tasks/thirdparty-tracing-task-external';

@Injectable({
  providedIn: 'root'
})
export class TracingPlanService {
  private apiEndPoint: string;

  constructor(
    private _http: HttpClient,
    private translate: TranslateService
  ) {
    this.apiEndPoint = environment.apiUrl;
  }

  getPagedTracingPlanItems(tracingPlanItemFilter: TracingPlanItemFilter): Observable<any>{
    return this._http
      .post(`${this.apiEndPoint}/Task/GetAllTracingPlans`, tracingPlanItemFilter)
      .pipe(
        catchError(err => {
          if (err.errorcode) {
            return _throw(err.errorcode);
          }
          return _throw(this.translate.instant('BUSSINESSERRORS.ErrUnexpected'));
        })
      );
  }

  getStatusCount(): Observable<TracingPlanStatusCount>{
    return this._http
    .get<TracingPlanStatusCount>(`${this.apiEndPoint}/Task/GetTracingPlansStatuses`)
    .pipe(
      map( res => res ),
      catchError(err => {
        if (err.errorcode) {
          return _throw(err.errorcode);
        }
        return _throw(this.translate.instant('BUSSINESSERRORS.ErrUnexpected'));
      })
    );
  }

  getAvailableStatus(): Observable<TracingPlanStatus[]>{
    return this._http
    .get<any[]>(`${this.apiEndPoint}/DashBoard/GetFollowUpPlanStatuses`)
    .pipe(
      map( res => {
        const planStatus: TracingPlanStatus[] = [];
        for (const key in res) {
          if (res.hasOwnProperty(key)) {
            const element: string = res[key];
            const status = new TracingPlanStatus();

            status.id = Number.parseInt(key);
            status.description = element;
            planStatus.push(status);
          }
        }
        return planStatus;
      }),
      catchError(err => {
        if (err.errorcode) {
          return _throw(err.errorcode);
        }
        return _throw(this.translate.instant('BUSSINESSERRORS.ErrUnexpected'));
      })
    );
  }

  getPlanTasks(tracingTaskFilter: TracingPlanTaskFilter): Observable<TracingPlanDetailItem> {
    return this._http
    .post<TracingPlanDetailItem>(`${this.apiEndPoint}/Task/GetAllTasks`, tracingTaskFilter)
    .pipe(
      map( (res:any) => {
        const tracingPlanDetail = res.results.find(x => x.tracingPlanId == tracingTaskFilter.tracingPlanId) as TracingPlanDetailItem;
        return tracingPlanDetail;
      } ),
      catchError(err => {
        if (err.errorcode) {
          return _throw(err.errorcode);
        }
        return _throw(this.translate.instant('BUSSINESSERRORS.ErrUnexpected'));
      })
    );
  }

  getPlanTaskDetail(planTaskId: number): Observable<TracingPlanTaskDetail> {
    const params = new HttpParams()
    .append("followUpTaskId", planTaskId.toString());

    return this._http
    .get<TracingPlanTaskDetail>(`${this.apiEndPoint}/Task/GetFollowUpTaskById`, { params })
    .pipe(
      map( (res:any) => {
        return res;
      } ),
      catchError(err => {
        if (err.errorcode) {
          return _throw(err.errorcode);
        }
        return _throw(this.translate.instant('BUSSINESSERRORS.ErrUnexpected'));
      })
    );
  }

  getPlanSubTaskDetail(planSubTaskId: number): Observable<TracingPlanTaskDetail> {
    const params = new HttpParams()
    .append("followUpSubTaskId", planSubTaskId.toString());

    return this._http
    .get<TracingPlanTaskDetail>(`${this.apiEndPoint}/Task/GetFollowUpSubTaskById`, { params })
    .pipe(
      map( (res:any) => {
        return res;
      } ),
      catchError(err => {
        if (err.errorcode) {
          return _throw(err.errorcode);
        }
        return _throw(this.translate.instant('BUSSINESSERRORS.ErrUnexpected'));
      })
    );
  }

  updatePlanTask(planTaskDetail: TracingPlanTaskDetail): Observable<any> {
    return this._http
    .post<any>(`${this.apiEndPoint}/Task/UpdateFollowUpTask`, planTaskDetail)
    .pipe(
      map( (res:any) => {
        return res
      } ),
      catchError(err => {
        if (err.errorcode) {
          return _throw(err.errorcode);
        }
        return _throw(this.translate.instant('BUSSINESSERRORS.ErrUnexpected'));
      })
    );
  }

  getInternalTaskById(taskId: number): Observable<ThirdPartyFollowUpTaskExternalDto> {
    return this._http.get<ThirdPartyFollowUpTaskExternalDto>(`${this.apiEndPoint}/FollowUpThirdPartyExternal/GetByIdInternal?taskId=${taskId}`)
  }


  iterateTask(task: TracingPlanTaskItem, iterationPeriod: number): Observable<any> {
    const tracingSubTaskCreateDto = {
      tracingTaskId: task.tracingPlanTaskId,
      taskDescription: task.taskDescription,
      iterativeDays: iterationPeriod
    };

    return this._http
    .post<any>(`${this.apiEndPoint}/Task/CreateIterativeSubTasks`, tracingSubTaskCreateDto)
    .pipe(
      map( res => {
        return res;
      } ),
      catchError(err => {
        if (err.errorcode) {
          return _throw(err.errorcode);
        }
        return _throw(this.translate.instant('BUSSINESSERRORS.ErrUnexpected'));
      })
    );
  }

  downloadTemplate(taskId, templateId): Observable<any> {
    return this._http.get(`${this.apiEndPoint}/Files/GetFileByFollowUpThirdPartyFomId?taskFormId=${taskId}&fileId=${templateId}`,
     {
        responseType: 'blob'
      });
  }

  downloadFile(taskId, fileId): Observable<any> {
    return this._http.get(`${this.apiEndPoint}/Files/GetFileByThirdPartyFollowUpFormId?taskThirdPartyFormId=${taskId}&fileId=${fileId}`,
     {
        responseType: 'blob'
      });
  }

}
